import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Users,
  User,
  UserX,
  MessageSquare,
  Brain,
  ShoppingCart,
  Star,
  Award,
  RefreshCw,
  FileDown,
  TrendingUp,
  Compass,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Laptop
} from 'lucide-react';
import { getExperiences, getLegacyUsersByIds } from '../services/legacyApiService';
import {
  getMobileAnalyticsEvents,
  MobileAnalyticsEventRow,
  UserPersonaRow,
} from '../services/mobileAnalyticsService';
import { LegacyExperience } from '../types/legacy';
import { useAuth } from '../contexts/AuthContext';
import { listRegisteredUsers, RegisteredUserItem } from '../services/registeredUsersService';

const EXCLUDED_EVENT_KEYWORDS = ['dialogflow', 'chat', 'message', 'gemini'];

const PROFILING_LABELS = {
  gender: {
    male: 'Maschio',
    female: 'Femmina',
    prefer_not_say: 'Non specificato',
  },
  mainInterest: {
    storia: 'Storia',
    arte: 'Arte',
    natura: 'Natura',
    enogastronomia: 'Enogastronomia',
    famiglia: 'Famiglia',
    curiosita: 'Curiosità / Storie locali',
    fotografia: 'Fotografia / Social',
    spiritualita: 'Spiritualità',
  },
  visitStyle: {
    rapida: 'Rapida',
    rilassata: 'Rilassata',
    approfondita: 'Approfondita',
    giocosa: 'Giocosa',
    avventurosa: 'Avventurosa',
  },
  companionType: {
    da_solo: 'Da solo',
    coppia: 'Coppia',
    famiglia_bambini: 'Famiglia con bambini',
    gruppo_amici: 'Gruppo di amici',
    scuola: 'Scuola',
    turista_senior: 'Turista senior',
  }
};

function readPayloadText(row: MobileAnalyticsEventRow, key: string): string {
  const value = row?.payload && typeof row.payload === 'object'
    ? (row.payload as Record<string, unknown>)[key]
    : undefined;
  return String(value ?? '').trim();
}

function readPayloadNumber(row: MobileAnalyticsEventRow, key: string): number {
  const text = readPayloadText(row, key);
  const num = Number(text);
  return Number.isFinite(num) ? num : 0;
}

function extractUserOrganizationId(user: Record<string, unknown> | null | undefined): number | null {
  if (!user || typeof user !== 'object') return null;
  const candidates = [
    user.id_organization,
    user.idOrganization,
    user.organizationId,
    (user.organization as Record<string, unknown> | undefined)?.id,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate || 0);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

function toInputDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const DEFAULT_FILTERS = () => {
  const toDate = toInputDate(new Date());
  const fromDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 7); // Set default to 7 days (ultima settimana) like the screenshot
    return toInputDate(d);
  })();
  return {
    rangePreset: 'week',
    fromDate,
    toDate,
    eventType: '',
  };
};

const Analytics = () => {
  const { user, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [experiences, setExperiences] = useState<LegacyExperience[]>([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS());
  const [mobileEvents, setMobileEvents] = useState<MobileAnalyticsEventRow[]>([]);
  const [userPersonas, setUserPersonas] = useState<UserPersonaRow[]>([]);
  const [userDirectory, setUserDirectory] = useState<Record<string, { displayName: string; email?: string }>>({});
  const [registeredUsersList, setRegisteredUsersList] = useState<RegisteredUserItem[]>([]);
  
  // Hover & Interactive state for Charts
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  // Pagination and sorting states for Registered Users
  const [userPage, setUserPage] = useState(1);
  const [userPageSize] = useState(15);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSortField, setUserSortField] = useState<'name' | 'email' | 'createdAt' | 'lastSeenAt'>('createdAt');
  const [userSortDirection, setUserSortDirection] = useState<'asc' | 'desc'>('desc');

  // Pagination and search for Recent Events
  const [eventPage, setEventPage] = useState(1);
  const [eventPageSize] = useState(15);
  const [eventSearchTerm, setEventSearchTerm] = useState('');

  const currentOrganizationId = useMemo(
    () => extractUserOrganizationId((user as Record<string, unknown> | null | undefined) || null),
    [user],
  );

  const personaMap = useMemo(() => {
    const map = new Map<string, UserPersonaRow>();
    userPersonas.forEach((p) => {
      if (p.id) {
        map.set(String(p.id).trim().toLowerCase(), p);
      }
    });
    return map;
  }, [userPersonas]);

  const loadExperiences = async () => {
    try {
      const rawExpList = await getExperiences();
      const expList = isSuperAdmin
        ? rawExpList
        : (currentOrganizationId
          ? rawExpList.filter((item) => Number(item.idOrganization || 0) === Number(currentOrganizationId))
          : []);
      setExperiences(expList);
    } catch (err) {
      console.error('Errore caricamento esperienze:', err);
    }
  };

  const loadMobileAnalytics = async (): Promise<MobileAnalyticsEventRow[]> => {
    try {
      const dateFrom = filters.fromDate;
      const dateTo = filters.toDate;
      const days = Math.max(
        1,
        Math.ceil((new Date(`${dateTo}T23:59:59.999`).getTime() - new Date(`${dateFrom}T00:00:00`).getTime() + 1) / (24 * 60 * 60 * 1000)),
      );
      const payload = await getMobileAnalyticsEvents({
        days,
        fromDate: dateFrom,
        toDate: dateTo,
        limit: 5000,
      });
      const events = payload.events || [];
      setMobileEvents(events);

      if (payload.userPersonas) {
        setUserPersonas(payload.userPersonas);
      }

      return events;
    } catch (err) {
      console.error('Errore caricamento eventi mobile:', err);
      return [];
    }
  };

  const loadRegisteredUsers = async () => {
    try {
      const pageSize = 500;
      const firstPage = await listRegisteredUsers({
        page: 1,
        pageSize,
      });

      const totalPages = Math.max(1, Number(firstPage.meta?.totalPages || 1));
      const maxPages = Math.min(totalPages, 20);
      const allUsers = [...(firstPage.users || [])];
      for (let page = 2; page <= maxPages; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const pageResult = await listRegisteredUsers({
          page,
          pageSize,
        });
        allUsers.push(...(pageResult.users || []));
      }
      setRegisteredUsersList(allUsers);

      const directory = allUsers.reduce<Record<string, { displayName: string; email?: string }>>((acc, item) => {
        const rawId = String(item.id || '').trim();
        const fullName = [item.firstName, item.lastName].filter(Boolean).join(' ').trim();
        const displayName = fullName || item.username || (rawId ? `Utente ${rawId}` : 'Utente');
        const entry = {
          displayName,
          email: item.email || undefined,
        };
        acc[rawId] = entry;
        if (item.username) acc[String(item.username).trim()] = entry;
        if (item.email) {
          acc[String(item.email).trim()] = entry;
          acc[String(item.email).trim().toLowerCase()] = entry;
        }
        return acc;
      }, {});
      setUserDirectory(directory);
    } catch (err) {
      console.warn('Registro utenti non disponibile:', err);
      setUserDirectory({});
    }
  };

  const loadLegacyIdentityOverrides = async (events: MobileAnalyticsEventRow[]) => {
    try {
      const unresolvedNumericIds = Array.from(
        new Set(
          (events || [])
            .map((item) => String(item.userId || '').trim())
            .filter((value) => /^\d+$/.test(value))
        ),
      ).map((value) => Number(value));
      if (unresolvedNumericIds.length === 0) return;
      const legacyIdentities = await getLegacyUsersByIds(unresolvedNumericIds);
      if (!legacyIdentities || Object.keys(legacyIdentities).length === 0) return;
      setUserDirectory((prev) => {
        const next = { ...prev };
        Object.entries(legacyIdentities).forEach(([id, identity]) => {
          if (!identity?.displayName) return;
          const entry = {
            displayName: identity.displayName,
            email: identity.email,
          };
          next[id] = entry;
          if (identity.username) {
            next[String(identity.username).trim()] = entry;
          }
        });
        return next;
      });
    } catch (err) {
      console.warn('Impossibile caricare identita legacy:', err);
    }
  };

  const loadAll = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await Promise.all([loadExperiences(), loadRegisteredUsers()]);
      const events = await loadMobileAnalytics();
      await loadLegacyIdentityOverrides(events);
    } catch (err) {
      console.error(err);
      setError('Si è verificato un errore durante l\'aggiornamento delle analytics.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const preset = e.target.value;
    const toDate = toInputDate(new Date());
    let fromDate = toDate;

    if (preset === 'week') {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      fromDate = toInputDate(d);
    } else if (preset === 'month') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      fromDate = toInputDate(d);
    } else if (preset === 'year') {
      const d = new Date();
      d.setDate(d.getDate() - 365);
      fromDate = toInputDate(d);
    } else {
      // Keep custom dates
      fromDate = filters.fromDate;
    }

    setFilters({
      rangePreset: preset,
      fromDate,
      toDate,
      eventType: filters.eventType,
    });
  };

  const handleRefresh = () => {
    void loadAll();
  };

  // Filter events based on active selection (all events or matching keyword)
  const filteredEvents = useMemo(() => {
    let list = mobileEvents;
    
    // Filter out internal system logs
    list = list.filter((row) => {
      const eventType = String(row.eventType || '').toLowerCase();
      if (!eventType) return false;
      return !EXCLUDED_EVENT_KEYWORDS.some((keyword) => eventType.includes(keyword) && eventType !== 'gemini_request' && eventType !== 'gemini_response');
    });

    if (filters.eventType) {
      list = list.filter((e) => e.eventType === filters.eventType);
    }

    return list;
  }, [mobileEvents, filters.eventType]);

  // Extract unique event types in loaded list for filter dropdown
  const uniqueEventTypes = useMemo(() => {
    const types = new Set<string>();
    mobileEvents.forEach(e => {
      if (e.eventType) {
        const lower = e.eventType.toLowerCase();
        if (!EXCLUDED_EVENT_KEYWORDS.some((keyword) => lower.includes(keyword) && lower !== 'gemini_request' && lower !== 'gemini_response')) {
          types.add(e.eventType);
        }
      }
    });
    return Array.from(types).sort();
  }, [mobileEvents]);

  // 1. Utenti Distinti, Registrati, Anonimi
  const uniqueRegisteredUsers = useMemo(() => {
    const set = new Set<string>();
    filteredEvents.forEach(e => {
      if (!e.isAnonymous && e.userId) set.add(String(e.userId));
    });
    return set;
  }, [filteredEvents]);

  const uniqueAnonymousSessions = useMemo(() => {
    const set = new Set<string>();
    filteredEvents.forEach(e => {
      if (e.isAnonymous && e.sessionId) set.add(String(e.sessionId));
    });
    return set;
  }, [filteredEvents]);

  const totalDistinctUsers = uniqueRegisteredUsers.size + uniqueAnonymousSessions.size;

  // 2. Sessioni
  const uniqueSessionsCount = useMemo(() => {
    return new Set(filteredEvents.map(e => e.sessionId).filter(Boolean)).size;
  }, [filteredEvents]);

  // 3. Risposte AI (gemini_response)
  const aiResponsesCount = useMemo(() => {
    return mobileEvents.filter(e => e.eventType === 'gemini_response').length;
  }, [mobileEvents]);

  // 4. Acquisti (purchase events, or cta click checkout/purchase)
  const purchasesCount = useMemo(() => {
    return mobileEvents.filter(e => {
      const lowerType = String(e.eventType || '').toLowerCase();
      const ctaType = readPayloadText(e, 'ctaType').toLowerCase();
      return lowerType.includes('purchase') || lowerType.includes('checkout') || ctaType.includes('purchase') || ctaType.includes('checkout') || ctaType.includes('buy');
    }).length;
  }, [mobileEvents]);

  // 5. Rating Stats
  const ratingStats = useMemo(() => {
    const rows = mobileEvents.filter((row) => String(row.eventType || '').toLowerCase() === 'experience_rating_submitted');
    if (!rows.length) {
      return { total: 0, avg: 0 };
    }
    let sum = 0;
    rows.forEach((row) => {
      const raw = readPayloadNumber(row, 'rating');
      const normalized = Math.max(1, Math.min(5, Math.round(raw || 0)));
      sum += normalized;
    });
    return {
      total: rows.length,
      avg: Number((sum / rows.length).toFixed(2)),
    };
  }, [mobileEvents]);

  // 6. Miglior Valutato
  const bestRatedBot = useMemo(() => {
    const ratings = mobileEvents.filter((row) => String(row.eventType || '').toLowerCase() === 'experience_rating_submitted');
    if (!ratings.length) {
      return { name: 'Nessun personaggio', details: 'Nessuna valutazione' };
    }

    const groups: Record<number, { sum: number; count: number; name: string }> = {};
    ratings.forEach((row) => {
      const expId = Number(row.idExperience || 0);
      if (!expId) return;
      const raw = readPayloadNumber(row, 'rating');
      const rating = Math.max(1, Math.min(5, Math.round(raw || 0)));
      
      if (!groups[expId]) {
        const exp = experiences.find(e => Number(e.id) === expId);
        groups[expId] = {
          sum: 0,
          count: 0,
          name: exp?.nomeChatbot || row.experienceName || `Personaggio #${expId}`
        };
      }
      groups[expId].sum += rating;
      groups[expId].count += 1;
    });

    const list = Object.values(groups).map((g) => ({
      name: g.name,
      avg: g.sum / g.count,
      count: g.count,
    })).sort((a, b) => {
      if (b.avg !== a.avg) return b.avg - a.avg;
      return b.count - a.count;
    });

    const top = list[0];
    if (!top) return { name: 'Nessun personaggio', details: 'Nessuna valutazione' };
    return {
      name: top.name,
      details: `Media: ${top.avg.toFixed(2)} / 5 (${top.count} voti)`
    };
  }, [mobileEvents, experiences]);

  // 7. Piu Utilizzato
  const mostUsedBot = useMemo(() => {
    const starts = mobileEvents.filter(e => e.eventType === 'experience_start' || e.eventType === 'session_start');
    if (!starts.length) {
      return { name: 'Nessuno', details: 'Nessuna attività' };
    }

    const counts: Record<number, { name: string; count: number }> = {};
    starts.forEach((row) => {
      const expId = Number(row.idExperience || 0);
      if (!expId) return;

      if (!counts[expId]) {
        const exp = experiences.find(e => Number(e.id) === expId);
        counts[expId] = {
          name: exp?.nomeChatbot || row.experienceName || `Personaggio #${expId}`,
          count: 0,
        };
      }
      counts[expId].count += 1;
    });

    const list = Object.values(counts).sort((a, b) => b.count - a.count);
    const top = list[0];
    if (!top) return { name: 'Nessuno', details: 'Nessuna attività' };
    return {
      name: top.name,
      details: `${top.count} sessioni attive`
    };
  }, [mobileEvents, experiences]);

  // App gradimento trend (defaults to rating average or 5.0)
  const appGradimentoTrend = ratingStats.avg > 0 ? ratingStats.avg.toFixed(2) : '5.00';
  const ratedBotsCount = useMemo(() => {
    return new Set(mobileEvents.filter(e => e.eventType === 'experience_rating_submitted').map(e => e.idExperience).filter(Boolean)).size;
  }, [mobileEvents]);

  // 8. Eventi nel Tempo Line Chart Data (curved spline area chart)
  const dailyEvents = useMemo(() => {
    const start = new Date(filters.fromDate);
    const end = new Date(filters.toDate);
    const dates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().slice(0, 10));
      current.setDate(current.getDate() + 1);
    }

    const counts = new Map<string, number>();
    filteredEvents.forEach((e) => {
      if (!e.createdAtIso) return;
      const day = e.createdAtIso.slice(0, 10);
      counts.set(day, (counts.get(day) || 0) + 1);
    });

    return dates.map((dateStr) => {
      const d = new Date(dateStr);
      const label = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
      return {
        date: dateStr,
        label,
        count: counts.get(dateStr) || 0,
      };
    });
  }, [filteredEvents, filters.fromDate, filters.toDate]);

  const maxCount = useMemo(() => {
    const counts = dailyEvents.map((d) => d.count);
    return Math.max(...counts, 1);
  }, [dailyEvents]);

  // Points for SVG path
  const points = useMemo(() => {
    const len = dailyEvents.length;
    return dailyEvents.map((d, idx) => {
      const x = len > 1 ? (idx / (len - 1)) * 440 + 30 : 250;
      const y = 160 - (d.count / maxCount) * 120;
      return { ...d, x, y };
    });
  }, [dailyEvents, maxCount]);

  // Build line & area path
  const d = useMemo(() => {
    if (points.length === 0) return '';
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const curr = points[i];
      const next = points[i + 1];
      const cp1x = curr.x + (next.x - curr.x) / 3;
      const cp1y = curr.y;
      const cp2x = curr.x + 2 * (next.x - curr.x) / 3;
      const cp2y = next.y;
      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`;
    }
    return path;
  }, [points]);

  const dArea = useMemo(() => {
    if (points.length === 0 || !d) return '';
    return `${d} L ${points[points.length - 1].x} 150 L ${points[0].x} 150 Z`;
  }, [points, d]);

  // 9. Trend Settimanale Table Data
  const weeklyTrend = useMemo(() => {
    const start = new Date(filters.fromDate);
    const end = new Date(filters.toDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const weeks: Array<{ label: string; start: Date; end: Date }> = [];
    if (diffDays <= 7) {
      weeks.push({
        label: `${start.toLocaleDateString('it-IT')} - ${end.toLocaleDateString('it-IT')}`,
        start: new Date(start),
        end: new Date(end)
      });
    } else {
      let currentEnd = new Date(end);
      while (currentEnd > start) {
        let currentStart = new Date(currentEnd);
        currentStart.setDate(currentStart.getDate() - 6);
        if (currentStart < start) {
          currentStart = new Date(start);
        }
        weeks.push({
          label: `${currentStart.toLocaleDateString('it-IT')} - ${currentEnd.toLocaleDateString('it-IT')}`,
          start: new Date(currentStart),
          end: new Date(currentEnd)
        });
        currentEnd.setDate(currentEnd.getDate() - 7);
      }
    }

    return weeks.map((w) => {
      const weekEvents = mobileEvents.filter((e) => {
        if (!e.createdAtIso) return false;
        const d = new Date(e.createdAtIso);
        return d >= w.start && d <= new Date(w.end.getTime() + 24 * 60 * 60 * 1000 - 1);
      });

      const uniqueRegistered = new Set(weekEvents.filter(e => !e.isAnonymous && e.userId).map(e => String(e.userId))).size;
      const uniqueAnonymous = new Set(weekEvents.filter(e => e.isAnonymous && e.sessionId).map(e => String(e.sessionId))).size;
      const totalDistinct = uniqueRegistered + uniqueAnonymous;

      return {
        label: w.label,
        totalDistinct,
        uniqueRegistered,
        uniqueAnonymous,
        growth: '--'
      };
    }).map((item, idx, arr) => {
      const prev = arr[idx + 1];
      if (prev && prev.totalDistinct > 0) {
        const growthVal = Math.round(((item.totalDistinct - prev.totalDistinct) / prev.totalDistinct) * 100);
        item.growth = growthVal > 0 ? `+${growthVal}%` : `${growthVal}%`;
      }
      return item;
    });
  }, [mobileEvents, filters.fromDate, filters.toDate]);

  // 10. Tipo Eventi Donut Chart Data
  const donutSegments = useMemo(() => {
    // Collect event type counts
    const eventTypeCounts: Record<string, number> = {};
    filteredEvents.forEach((row) => {
      const eventType = String(row.eventType || 'unknown').toLowerCase();
      eventTypeCounts[eventType] = (eventTypeCounts[eventType] || 0) + 1;
    });

    const types = [
      { key: 'gemini_request', label: 'gemini_request', color: '#22c55e' },
      { key: 'gemini_response', label: 'gemini_response', color: '#3b82f6' },
      { key: 'session_start', label: 'session_start', color: '#f97316' },
      { key: 'chat_started', label: 'chat_started', color: '#a855f7' },
      { key: 'chat_ended', label: 'chat_ended', color: '#ef4444' },
    ];

    let total = 0;
    const counts = types.map((t) => {
      const count = eventTypeCounts[t.key] || 0;
      total += count;
      return { ...t, count };
    });

    const handledKeys = new Set(types.map(t => t.key));
    let othersCount = 0;
    Object.entries(eventTypeCounts).forEach(([key, count]) => {
      if (!handledKeys.has(key)) othersCount += count;
    });

    if (othersCount > 0) {
      counts.push({ key: 'others', label: 'Altri eventi', color: '#94a3b8', count: othersCount });
      total += othersCount;
    }

    let accumulatedPercent = 0;
    return counts.filter(c => c.count > 0).map((item) => {
      const percent = total > 0 ? item.count / total : 0;
      const strokeDasharray = `${percent * 502.65} 502.65`;
      const strokeDashoffset = -accumulatedPercent * 502.65;
      accumulatedPercent += percent;
      return {
        ...item,
        percent,
        strokeDasharray,
        strokeDashoffset,
      };
    });
  }, [filteredEvents]);


  // 12. Utenti Registrati Table Logic (client-side searching, sorting, pagination)
  const sortedAndFilteredUsers = useMemo(() => {
    let list = registeredUsersList;

    if (userSearchTerm.trim()) {
      const term = userSearchTerm.toLowerCase().trim();
      list = list.filter((u) => {
        const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').toLowerCase();
        return (
          u.username?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term) ||
          fullName.includes(term) ||
          u.id?.toLowerCase().includes(term)
        );
      });
    }

    list = [...list].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      if (userSortField === 'name') {
        aVal = [a.firstName, a.lastName].filter(Boolean).join(' ').toLowerCase() || a.username?.toLowerCase() || '';
        bVal = [b.firstName, b.lastName].filter(Boolean).join(' ').toLowerCase() || b.username?.toLowerCase() || '';
      } else if (userSortField === 'email') {
        aVal = a.email?.toLowerCase() || '';
        bVal = b.email?.toLowerCase() || '';
      } else if (userSortField === 'createdAt') {
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      } else if (userSortField === 'lastSeenAt') {
        aVal = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0;
        bVal = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0;
      }

      if (aVal < bVal) return userSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return userSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [registeredUsersList, userSearchTerm, userSortField, userSortDirection]);

  const paginatedUsers = useMemo(() => {
    const startIdx = (userPage - 1) * userPageSize;
    return sortedAndFilteredUsers.slice(startIdx, startIdx + userPageSize);
  }, [sortedAndFilteredUsers, userPage, userPageSize]);

  const userTotalPages = Math.max(1, Math.ceil(sortedAndFilteredUsers.length / userPageSize));

  const handleUserSort = (field: typeof userSortField) => {
    if (userSortField === field) {
      setUserSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setUserSortField(field);
      setUserSortDirection('desc');
    }
    setUserPage(1);
  };

  const resolveGenderLabel = (userId: string) => {
    const key = String(userId).trim().toLowerCase();
    const persona = personaMap.get(key);
    if (!persona?.gender) return '-';
    return PROFILING_LABELS.gender[persona.gender as keyof typeof PROFILING_LABELS.gender] || '-';
  };



  // 13. Eventi Recenti Table Logic (client-side search & pagination)
  const sortedAndFilteredEvents = useMemo(() => {
    let list = filteredEvents;

    if (eventSearchTerm.trim()) {
      const term = eventSearchTerm.toLowerCase().trim();
      list = list.filter((e) => {
        const rawUserId = String(e.userId || '').trim();
        const resolvedUser = userDirectory[rawUserId];
        const actorName = resolvedUser?.displayName?.toLowerCase() || 'utente anonimo';
        const actorEmail = resolvedUser?.email?.toLowerCase() || '';
        const details = getEventDetails(e).toLowerCase();
        
        return (
          e.eventType?.toLowerCase().includes(term) ||
          e.platform?.toLowerCase().includes(term) ||
          actorName.includes(term) ||
          actorEmail.includes(term) ||
          details.includes(term)
        );
      });
    }

    // Sort by date desc
    return [...list].sort((a, b) => {
      const aTime = a.createdAtIso ? new Date(a.createdAtIso).getTime() : 0;
      const bTime = b.createdAtIso ? new Date(b.createdAtIso).getTime() : 0;
      return bTime - aTime;
    });
  }, [filteredEvents, eventSearchTerm, userDirectory]);

  const paginatedEvents = useMemo(() => {
    const startIdx = (eventPage - 1) * eventPageSize;
    return sortedAndFilteredEvents.slice(startIdx, startIdx + eventPageSize);
  }, [sortedAndFilteredEvents, eventPage, eventPageSize]);

  const eventTotalPages = Math.max(1, Math.ceil(sortedAndFilteredEvents.length / eventPageSize));

  function getEventDetails(row: MobileAnalyticsEventRow): string {
    const type = String(row.eventType || '').toLowerCase();
    const exp = experiences.find(e => Number(e.id) === row.idExperience || e.name === row.experienceName);
    const characterName = exp?.nomeChatbot || row.experienceName || 'Genius Loci';

    if (type === 'gemini_request' || type === 'gemini_response') {
      return `Personaggio: ${characterName}`;
    }
    if (type === 'experience_open' || type === 'experience_start' || type === 'experience_complete') {
      return `Percorso: ${row.experienceName || exp?.name || 'n/d'}`;
    }
    if (type === 'cta_click') {
      const cta = readPayloadText(row, 'ctaType') || 'cta_generica';
      return `CTA: ${cta.replace(/_/g, ' ')}`;
    }
    if (type === 'experience_rating_submitted') {
      const score = readPayloadNumber(row, 'rating');
      return `Personaggio: ${characterName} (Valutazione: ${score}/5)`;
    }
    return `Dettagli: ${row.eventType || 'Azione applicativa'}`;
  }

  const resolveActorLabel = (row: MobileAnalyticsEventRow) => {
    if (row.isAnonymous) return 'Utente anonimo';
    const rawUserId = String(row.userId || '').trim();
    if (!rawUserId) return 'Utente non disponibile';
    const resolved = userDirectory[rawUserId];
    return resolved ? `${resolved.displayName}` : `Utente (${rawUserId})`;
  };

  const onExportCsv = async () => {
    try {
      setExporting(true);
      if (!filteredEvents.length) {
        setError('Nessun evento disponibile per l\'esportazione.');
        return;
      }

      const headers = [
        'id',
        'eventType',
        'sessionId',
        'idExperience',
        'experienceName',
        'userId',
        'isAnonymous',
        'platform',
        'createdAtIso',
      ];
      const esc = (v: unknown) => {
        const s = String(v ?? '');
        const safe = s.replace(/"/g, '""');
        return `"${safe}"`;
      };
      const csv = [
        headers.join(','),
        ...filteredEvents.map((u) => [
          u.id,
          u.eventType,
          u.sessionId || '',
          u.idExperience || '',
          u.experienceName || '',
          u.userId || '',
          u.isAnonymous,
          u.platform || '',
          u.createdAtIso || '',
        ].map(esc).join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_eventi_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError('Esportazione CSV non riuscita.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-3xl font-extrabold text-text-primary flex items-center gap-2.5">
            <Activity size={28} className="text-emerald-600" />
            Analytics
          </h1>
          <p className="text-xs text-text-secondary mt-1">
            Monitoraggio utenti, sessioni e attività della piattaforma
          </p>
        </div>
        
        <button
          onClick={onExportCsv}
          disabled={exporting || loading}
          className="self-start border border-border bg-white text-text-secondary hover:bg-slate-50 hover:text-text-primary px-4 py-2.5 text-xs rounded-lg flex items-center gap-2 font-semibold shadow-sm transition-all disabled:opacity-50"
        >
          <FileDown size={14} className={exporting ? 'animate-bounce' : ''} />
          <span>Esporta CSV</span>
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-error/30 bg-error/10 p-4 text-error text-sm">{error}</div>
      )}

      {/* KPI Cards Grid (9 items) */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-4">
        {/* Card 1: Utenti Distinti */}
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center shrink-0">
            <Users size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-2xl font-bold leading-none text-text-primary">{loading ? '–' : totalDistinctUsers}</p>
            <p className="text-[10px] font-semibold text-text-secondary mt-1.5 leading-tight">Utenti Distinti</p>
            <p className="text-[9px] text-text-muted mt-0.5 leading-none">Registrati {loading ? '–' : uniqueRegisteredUsers.size} / Anonimi {loading ? '–' : uniqueAnonymousSessions.size}</p>
          </div>
        </div>

        {/* Card 2: Utenti Registrati */}
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0">
            <User size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-2xl font-bold leading-none text-text-primary">{loading ? '–' : uniqueRegisteredUsers.size}</p>
            <p className="text-[10px] font-semibold text-text-secondary mt-1.5 leading-tight">Utenti</p>
            <p className="text-[9px] text-text-muted mt-0.5 leading-none">Registrati</p>
          </div>
        </div>

        {/* Card 3: Utenti Anonimi */}
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
            <UserX size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-2xl font-bold leading-none text-text-primary">{loading ? '–' : uniqueAnonymousSessions.size}</p>
            <p className="text-[10px] font-semibold text-text-secondary mt-1.5 leading-tight">Utenti Anonimi</p>
          </div>
        </div>

        {/* Card 4: Sessioni */}
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-600 flex items-center justify-center shrink-0">
            <MessageSquare size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-2xl font-bold leading-none text-text-primary">{loading ? '–' : uniqueSessionsCount}</p>
            <p className="text-[10px] font-semibold text-text-secondary mt-1.5 leading-tight">Sessioni</p>
          </div>
        </div>

        {/* Card 5: Risposte AI */}
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/10 text-teal-600 flex items-center justify-center shrink-0">
            <Brain size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-2xl font-bold leading-none text-text-primary">{loading ? '–' : aiResponsesCount}</p>
            <p className="text-[10px] font-semibold text-text-secondary mt-1.5 leading-tight">Risposte AI</p>
          </div>
        </div>

        {/* Card 6: Acquisti */}
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 text-red-600 flex items-center justify-center shrink-0">
            <ShoppingCart size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-2xl font-bold leading-none text-text-primary">{loading ? '–' : purchasesCount}</p>
            <p className="text-[10px] font-semibold text-text-secondary mt-1.5 leading-tight">Acquisti</p>
          </div>
        </div>

        {/* Card 7: Voti Ricevuti */}
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
            <Star size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-2xl font-bold leading-none text-text-primary">{loading ? '–' : ratingStats.total}</p>
            <p className="text-[10px] font-semibold text-text-secondary mt-1.5 leading-tight">Voti Ricevuti</p>
          </div>
        </div>

        {/* Card 8: Miglior Valutato */}
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0 animate-bounce-gentle">
            <Star size={18} className="fill-amber-600 text-amber-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-text-primary leading-none truncate" title={bestRatedBot.name}>{loading ? '–' : bestRatedBot.name}</p>
            <p className="text-[10px] font-semibold text-text-secondary mt-1.5 leading-tight truncate">Miglior Valutato</p>
            <p className="text-[9px] text-text-muted mt-0.5 leading-none truncate" title={bestRatedBot.details}>{loading ? '–' : bestRatedBot.details}</p>
          </div>
        </div>

        {/* Card 9: Piu Utilizzato */}
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0">
            <Award size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-text-primary leading-none truncate" title={mostUsedBot.name}>{loading ? '–' : mostUsedBot.name}</p>
            <p className="text-[10px] font-semibold text-text-secondary mt-1.5 leading-tight truncate">Più Utilizzato</p>
            <p className="text-[9px] text-text-muted mt-0.5 leading-none truncate" title={mostUsedBot.details}>{loading ? '–' : mostUsedBot.details}</p>
          </div>
        </div>
      </div>

      {/* Filter Bar Card */}
      <div className="card p-4 bg-white flex flex-col md:flex-row md:items-end justify-between gap-4">
        {/* Periodo */}
        <div className="flex-1 min-w-[150px]">
          <label className="text-[11px] font-bold text-text-secondary uppercase tracking-wider block mb-1">Periodo</label>
          <select
            value={filters.rangePreset}
            onChange={handlePresetChange}
            className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-text-primary focus:border-primary transition-all font-medium"
          >
            <option value="week">Ultima settimana</option>
            <option value="month">Ultimo mese</option>
            <option value="year">Ultimo anno</option>
            <option value="custom">Personalizzato</option>
          </select>
        </div>

        {/* Data Da */}
        <div className="flex-1 min-w-[120px]">
          <label className="text-[11px] font-bold text-text-secondary uppercase tracking-wider block mb-1">Data Da</label>
          <input
            type="date"
            value={filters.fromDate}
            disabled={filters.rangePreset !== 'custom'}
            onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value }))}
            className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-text-primary disabled:bg-slate-50 disabled:text-text-muted transition-all font-medium"
          />
        </div>

        {/* Data A */}
        <div className="flex-1 min-w-[120px]">
          <label className="text-[11px] font-bold text-text-secondary uppercase tracking-wider block mb-1">Data A</label>
          <input
            type="date"
            value={filters.toDate}
            disabled={filters.rangePreset !== 'custom'}
            onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value }))}
            className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-text-primary disabled:bg-slate-50 disabled:text-text-muted transition-all font-medium"
          />
        </div>

        {/* Tipo Evento */}
        <div className="flex-1 min-w-[150px]">
          <label className="text-[11px] font-bold text-text-secondary uppercase tracking-wider block mb-1">Tipo Evento</label>
          <select
            value={filters.eventType}
            onChange={(e) => setFilters(prev => ({ ...prev, eventType: e.target.value }))}
            className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-text-primary focus:border-primary transition-all font-medium"
          >
            <option value="">Tutti</option>
            {uniqueEventTypes.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* Aggiorna Button */}
        <div>
          <button
            onClick={handleRefresh}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm shadow-sm"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            <span>Aggiorna</span>
          </button>
        </div>
      </div>

      {/* Sub-bar Trend Badges */}
      <div className="flex flex-wrap gap-2.5">
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-3 py-1 text-xs flex items-center gap-1.5 font-medium shadow-sm">
          <Star size={12} className="fill-amber-400 text-amber-500" />
          <span>Trend gradimento app: {appGradimentoTrend} / 5</span>
        </div>
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-3 py-1 text-xs flex items-center gap-1.5 font-medium shadow-sm">
          <Star size={12} className="fill-amber-400 text-amber-500" />
          <span>Trend ultimo: {ratedBotsCount} personaggi valutati</span>
        </div>
      </div>

      {/* Main Grid: Events & Weeks on Left, Event Types Donut on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column (8/12) */}
        <div className="lg:col-span-8 space-y-6">
          {/* Eventi nel Tempo (Line Chart) */}
          <div className="card p-5 bg-white">
            <h3 className="font-semibold text-text-primary text-sm flex items-center gap-2 mb-4 border-b border-border pb-2">
              <Activity size={16} className="text-emerald-500" />
              Eventi nel Tempo
            </h3>
            
            <div className="mb-4 flex items-center gap-2">
              <input
                type="checkbox"
                id="allEventsCheck"
                checked={filters.eventType === ''}
                onChange={(e) => setFilters(prev => ({ ...prev, eventType: e.target.checked ? '' : 'explore_open' }))}
                className="rounded text-emerald-600 focus:ring-emerald-500 border-border h-4 w-4"
              />
              <label htmlFor="allEventsCheck" className="text-xs font-semibold text-text-secondary cursor-pointer select-none">
                Tutti gli eventi
              </label>
            </div>

            {/* SVG Line / Area Chart */}
            <div className="relative w-full h-48 mt-2 select-none">
              {loading ? (
                <div className="w-full h-full flex items-center justify-center text-text-muted text-xs italic">
                  Caricamento in corso...
                </div>
              ) : dailyEvents.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center text-text-muted text-xs italic">
                  Nessun dato disponibile
                </div>
              ) : (
                <svg viewBox="0 0 500 180" width="100%" height="100%" preserveAspectRatio="none">
                  {/* Grid Lines */}
                  <line x1="20" y1="30" x2="480" y2="30" stroke="#f1f5f9" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="20" y1="70" x2="480" y2="70" stroke="#f1f5f9" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="20" y1="110" x2="480" y2="110" stroke="#f1f5f9" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="20" y1="150" x2="480" y2="150" stroke="#e2e8f0" strokeWidth="1.5" />

                  {/* Area Fill */}
                  {dArea && <path d={dArea} fill="url(#chartEmeraldGradient)" opacity="0.1" />}

                  {/* Curve Line */}
                  {d && <path d={d} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}

                  {/* Interaction rects */}
                  {points.map((pt, idx) => {
                    const rectW = 460 / dailyEvents.length;
                    return (
                      <rect
                        key={idx}
                        x={pt.x - rectW / 2}
                        y={10}
                        width={rectW}
                        height={140}
                        fill="transparent"
                        onMouseEnter={() => setHoveredIndex(idx)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        className="cursor-crosshair"
                      />
                    );
                  })}

                  {/* Guide and Vertices on hover */}
                  {hoveredIndex !== null && points[hoveredIndex] && (
                    <>
                      <line
                        x1={points[hoveredIndex].x}
                        y1={30}
                        x2={points[hoveredIndex].x}
                        y2={150}
                        stroke="#cbd5e1"
                        strokeWidth="1"
                        strokeDasharray="4 4"
                      />
                      <circle
                        cx={points[hoveredIndex].x}
                        cy={points[hoveredIndex].y}
                        r="5.5"
                        fill="#10b981"
                        stroke="#ffffff"
                        strokeWidth="1.5"
                      />
                    </>
                  )}

                  <defs>
                    <linearGradient id="chartEmeraldGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
              )}

              {/* Tooltip Overlay */}
              {hoveredIndex !== null && points[hoveredIndex] && (
                <div
                  className="absolute bg-slate-900/95 text-white text-[10px] font-semibold px-2 py-1 rounded shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full flex flex-col items-center gap-0.5 border border-slate-700"
                  style={{
                    left: `${(points[hoveredIndex].x / 500) * 100}%`,
                    top: `${(points[hoveredIndex].y / 180) * 100 - 8}%`,
                  }}
                >
                  <span className="opacity-75">{points[hoveredIndex].date}</span>
                  <span className="font-bold text-emerald-400">{points[hoveredIndex].count} eventi</span>
                </div>
              )}
            </div>

            {/* Date Labels */}
            <div className="flex justify-between px-5 text-[9px] text-text-secondary mt-1.5 font-semibold">
              {dailyEvents.map((d, idx) => {
                if (dailyEvents.length > 10 && idx % Math.ceil(dailyEvents.length / 8) !== 0 && idx !== dailyEvents.length - 1) {
                  return null;
                }
                const parts = d.date.split('-');
                const formatted = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d.date;
                return <span key={d.date}>{formatted}</span>;
              })}
            </div>
          </div>

          {/* Trend Settimanale Utenti */}
          <div className="card p-5 bg-white">
            <h3 className="font-semibold text-text-primary text-sm flex items-center gap-2 mb-4 border-b border-border pb-2">
              <TrendingUp size={16} className="text-primary" />
              Trend Settimanale Utenti
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 text-text-secondary text-[10px] uppercase tracking-wider font-bold">
                  <tr>
                    <th className="px-4 py-2 border-b border-border">Settimana</th>
                    <th className="px-4 py-2 border-b border-border text-center">Utenti Distinti</th>
                    <th className="px-4 py-2 border-b border-border text-center">Registrati</th>
                    <th className="px-4 py-2 border-b border-border text-center">Anonimi</th>
                    <th className="px-4 py-2 border-b border-border text-center">Crescita %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-text-primary">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-4 text-center text-text-secondary">Caricamento...</td>
                    </tr>
                  ) : weeklyTrend.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-4 text-center text-text-muted italic">Nessun dato settimanale</td>
                    </tr>
                  ) : (
                    weeklyTrend.map((row, index) => (
                      <tr key={index} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3 font-semibold">{row.label}</td>
                        <td className="px-4 py-3 text-center font-bold text-slate-800">{row.totalDistinct}</td>
                        <td className="px-4 py-3 text-center text-text-secondary">{row.uniqueRegistered}</td>
                        <td className="px-4 py-3 text-center text-text-secondary">{row.uniqueAnonymous}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded font-semibold text-[10px] ${
                            row.growth.startsWith('+') ? 'bg-green-50 text-green-700' : (row.growth.startsWith('-') ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600')
                          }`}>
                            {row.growth}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column (4/12) */}
        <div className="lg:col-span-4 flex flex-col">
          {/* Tipo Eventi (Donut Chart) */}
          <div className="card p-5 bg-white flex-1 flex flex-col justify-between">
            <div>
              <h3 className="font-semibold text-text-primary text-sm flex items-center gap-2 mb-4 border-b border-border pb-2">
                <Compass size={16} className="text-primary" />
                Tipo Eventi
              </h3>
              
              {/* Donut Chart SVG */}
              <div className="relative flex items-center justify-center h-44 my-4">
                {loading ? (
                  <div className="text-text-muted text-xs italic">Caricamento...</div>
                ) : donutSegments.length === 0 ? (
                  <div className="text-text-muted text-xs italic">Nessun dato</div>
                ) : (
                  <svg width="170" height="170" viewBox="0 0 200 200" className="transform -rotate-90">
                    {donutSegments.map((seg, idx) => (
                      <circle
                        key={seg.key}
                        cx="100"
                        cy="100"
                        r="80"
                        fill="none"
                        stroke={seg.color}
                        strokeWidth="28"
                        strokeDasharray={seg.strokeDasharray}
                        strokeDashoffset={seg.strokeDashoffset}
                        className="transition-all duration-300 hover:stroke-[34px] cursor-pointer"
                        onMouseEnter={() => setHoveredSegment(idx)}
                        onMouseLeave={() => setHoveredSegment(null)}
                      />
                    ))}
                    <circle cx="100" cy="100" r="64" fill="#ffffff" />
                  </svg>
                )}
                
                {/* Center tooltip label */}
                <div className="absolute text-center select-none pointer-events-none">
                  {hoveredSegment !== null && donutSegments[hoveredSegment] ? (
                    <>
                      <p className="text-[9px] font-bold text-text-secondary uppercase tracking-wider max-w-[100px] truncate">{donutSegments[hoveredSegment].label}</p>
                      <p className="text-xl font-extrabold text-text-primary mt-0.5">{donutSegments[hoveredSegment].count}</p>
                      <p className="text-[9px] font-semibold text-emerald-600">({Math.round(donutSegments[hoveredSegment].percent * 100)}%)</p>
                    </>
                  ) : (
                    <>
                      <p className="text-[9px] font-bold text-text-secondary uppercase tracking-wider">Totale</p>
                      <p className="text-2xl font-black text-text-primary mt-0.5">{loading ? '–' : filteredEvents.length}</p>
                      <p className="text-[9px] text-text-muted uppercase tracking-widest mt-0.5">Eventi</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="space-y-1.5 mt-4 border-t border-slate-50 pt-3">
              {donutSegments.map((seg, idx) => (
                <div
                  key={seg.key}
                  className={`flex items-center justify-between text-[11px] px-2 py-1 rounded transition-colors duration-150 cursor-pointer ${
                    hoveredSegment === idx ? 'bg-slate-50 font-bold' : ''
                  }`}
                  onMouseEnter={() => setHoveredSegment(idx)}
                  onMouseLeave={() => setHoveredSegment(null)}
                >
                  <div className="flex items-center gap-2 truncate">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
                    <span className="text-text-secondary truncate">{seg.label}</span>
                  </div>
                  <span className="font-semibold text-text-primary ml-2">{seg.count} ({Math.round(seg.percent * 100)}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Utenti Registrati */}
      <div className="card p-5 bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-border pb-3 mb-4 gap-3">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-text-primary text-sm flex items-center gap-2">
              <Users size={16} className="text-primary" />
              Utenti Registrati
            </h3>
            <span className="bg-slate-100 text-text-secondary text-xs px-2.5 py-0.5 rounded-full font-bold">
              {loading ? '–' : sortedAndFilteredUsers.length}
            </span>
          </div>
          
          {/* Table Search */}
          <div className="relative w-full sm:w-60">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              placeholder="Cerca utente..."
              value={userSearchTerm}
              onChange={(e) => {
                setUserSearchTerm(e.target.value);
                setUserPage(1);
              }}
              className="w-full rounded-lg border border-border pl-8 pr-3 py-1.5 text-xs bg-white text-text-primary focus:border-primary focus:outline-none transition-all"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 text-text-secondary text-[10px] uppercase tracking-wider font-bold">
              <tr>
                <th className="px-4 py-2.5 border-b border-border cursor-pointer hover:bg-slate-100" onClick={() => handleUserSort('name')}>
                  <div className="flex items-center gap-1.5">
                    <span>Nome</span>
                    <ArrowUpDown size={10} />
                  </div>
                </th>
                <th className="px-4 py-2.5 border-b border-border cursor-pointer hover:bg-slate-100" onClick={() => handleUserSort('email')}>
                  <div className="flex items-center gap-1.5">
                    <span>Email</span>
                    <ArrowUpDown size={10} />
                  </div>
                </th>
                <th className="px-4 py-2.5 border-b border-border text-center">Sesso</th>
                <th className="px-4 py-2.5 border-b border-border text-center">Interesse</th>
                <th className="px-4 py-2.5 border-b border-border text-center">Stile</th>
                <th className="px-4 py-2.5 border-b border-border text-center">Compagnia</th>
                <th className="px-4 py-2.5 border-b border-border cursor-pointer hover:bg-slate-100" onClick={() => handleUserSort('lastSeenAt')}>
                  <div className="flex items-center gap-1.5">
                    <span>Ultimo Accesso</span>
                    <ArrowUpDown size={10} />
                  </div>
                </th>
                <th className="px-4 py-2.5 border-b border-border cursor-pointer hover:bg-slate-100" onClick={() => handleUserSort('createdAt')}>
                  <div className="flex items-center gap-1.5">
                    <span>Registrazione</span>
                    <ArrowUpDown size={10} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-text-primary">
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx}>
                    <td colSpan={8} className="px-4 py-4 text-center"><div className="h-4 bg-slate-50 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-text-muted italic">Nessun utente trovato</td>
                </tr>
              ) : (
                paginatedUsers.map((item) => {
                  const fullName = [item.firstName, item.lastName].filter(Boolean).join(' ');
                  const displayName = fullName || item.username || `Utente #${item.id}`;
                  const formattedEmail = item.email || 'Non specificato';
                  
                  const key = String(item.id).trim().toLowerCase();
                  const persona = personaMap.get(key);
                  const formatProfiling = (group: 'mainInterest' | 'visitStyle' | 'companionType', val?: string | null) => {
                    if (!val) return '-';
                    const groupLabels = PROFILING_LABELS[group];
                    const label = groupLabels[val as keyof typeof groupLabels];
                    if (label) return label;
                    const cleaned = val.replace(/_/g, ' ').trim();
                    if (!cleaned) return '-';
                    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
                  };

                  return (
                    <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3 font-semibold">{displayName}</td>
                      <td className="px-4 py-3 text-text-secondary">{formattedEmail}</td>
                      <td className="px-4 py-3 text-center">{resolveGenderLabel(item.id)}</td>
                      <td className="px-4 py-3 text-center text-text-secondary">{formatProfiling('mainInterest', persona?.mainInterest)}</td>
                      <td className="px-4 py-3 text-center text-text-secondary">{formatProfiling('visitStyle', persona?.visitStyle)}</td>
                      <td className="px-4 py-3 text-center text-text-secondary">{formatProfiling('companionType', persona?.companionType)}</td>
                      <td className="px-4 py-3 text-text-secondary">
                        {item.lastSeenAt ? new Date(item.lastSeenAt).toLocaleString('it-IT') : '-'}
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        {item.createdAt ? new Date(item.createdAt).toLocaleString('it-IT') : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* User Pagination footer */}
        {!loading && userTotalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border mt-3 pt-3 text-xs">
            <span className="text-text-secondary">
              Pagina <strong>{userPage}</strong> di <strong>{userTotalPages}</strong> ({sortedAndFilteredUsers.length} utenti)
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setUserPage(p => Math.max(1, p - 1))}
                disabled={userPage === 1}
                className="p-1.5 rounded-lg border border-border bg-white text-text-secondary hover:bg-slate-50 disabled:opacity-40"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setUserPage(p => Math.min(userTotalPages, p + 1))}
                disabled={userPage === userTotalPages}
                className="p-1.5 rounded-lg border border-border bg-white text-text-secondary hover:bg-slate-50 disabled:opacity-40"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Eventi Recenti */}
      <div className="card p-5 bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-border pb-3 mb-4 gap-3">
          <h3 className="font-semibold text-text-primary text-sm flex items-center gap-2">
            <Activity size={16} className="text-primary" />
            Eventi Recenti
          </h3>
          
          {/* Event Search */}
          <div className="relative w-full sm:w-60">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              placeholder="Cerca eventi..."
              value={eventSearchTerm}
              onChange={(e) => {
                setEventSearchTerm(e.target.value);
                setEventPage(1);
              }}
              className="w-full rounded-lg border border-border pl-8 pr-3 py-1.5 text-xs bg-white text-text-primary focus:border-primary focus:outline-none transition-all"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 text-text-secondary text-[10px] uppercase tracking-wider font-bold">
              <tr>
                <th className="px-4 py-2.5 border-b border-border">Data/Ora</th>
                <th className="px-4 py-2.5 border-b border-border">Evento</th>
                <th className="px-4 py-2.5 border-b border-border">Utente</th>
                <th className="px-4 py-2.5 border-b border-border">Dettagli</th>
                <th className="px-4 py-2.5 border-b border-border text-center">Piattaforma</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-text-primary">
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx}>
                    <td colSpan={5} className="px-4 py-4 text-center"><div className="h-4 bg-slate-50 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : paginatedEvents.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-text-muted italic">Nessun evento registrato</td>
                </tr>
              ) : (
                paginatedEvents.map((row) => {
                  const labelColor = String(row.eventType || '').toLowerCase() === 'gemini_response' ? 'bg-blue-50 text-blue-700' :
                                    String(row.eventType || '').toLowerCase() === 'gemini_request' ? 'bg-green-50 text-green-700' :
                                    String(row.eventType || '').toLowerCase() === 'experience_start' ? 'bg-purple-50 text-purple-700' :
                                    'bg-slate-100 text-slate-600';

                  return (
                    <tr key={row.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3 text-text-secondary">
                        {row.createdAtIso ? new Date(row.createdAtIso).toLocaleString('it-IT') : '-'}
                      </td>
                      <td className="px-4 py-3 font-semibold">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${labelColor}`}>
                          {row.eventType}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-text-secondary">{resolveActorLabel(row)}</td>
                      <td className="px-4 py-3 text-text-secondary">{getEventDetails(row)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-text-secondary bg-slate-100 px-2 py-0.5 rounded">
                          <Laptop size={10} />
                          {row.platform || 'iOS'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Event Pagination footer */}
        {!loading && eventTotalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border mt-3 pt-3 text-xs">
            <span className="text-text-secondary">
              Pagina <strong>{eventPage}</strong> di <strong>{eventTotalPages}</strong> ({sortedAndFilteredEvents.length} eventi)
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setEventPage(p => Math.max(1, p - 1))}
                disabled={eventPage === 1}
                className="p-1.5 rounded-lg border border-border bg-white text-text-secondary hover:bg-slate-50 disabled:opacity-40"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setEventPage(p => Math.min(eventTotalPages, p + 1))}
                disabled={eventPage === eventTotalPages}
                className="p-1.5 rounded-lg border border-border bg-white text-text-secondary hover:bg-slate-50 disabled:opacity-40"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
